import { Typography, Divider, Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useSnackbar } from 'notistack';
import { useState, Fragment } from 'react';
import cuid from 'cuid';
import { DocumentType } from '@models/document';
import { PostArtifact } from '@models/note';
import { DataType } from '@services/data/productModelTypes';
import { useDeleteDocument, useCreateDocument, documentQueryKey } from '@services/document';
import { useSearchDocuments } from '@services/document/useSearchDocuments';
import { useCreateFile, useUpdateFile, useDeleteFile, uploadFile } from '@services/file';
import { acceptedFileMimeTypes } from '@utils/fileUtils';
import { AddFilesDialog } from '@components/dialog/AddFilesDialog';
import { ToastType } from '@components/toast/ToastType.enum';
import { useSaveFields } from '@pages/QuoteDetails/helpers/useSaveFields';
import Loading from '@components/Loading';
import { MAX_FILES_AMOUNT } from '@components/drawerItem/DrawerItem.constants';
import { FileListItem } from './FileListItem';
import { UploadFileButton } from './UploadFileButton';
import { QFCTypeExecuted } from '../QuoteFieldControl';

const descriptionMaxLength = 80;

export type DocumentValue = {
  document: {
    documentId: string;
  };
};

export type DocumentListValue = {
  documents: DocumentValue['document'][];
};

function isDocumentListValue(value?: any): value is DocumentListValue {
  return !value || value.documents !== undefined;
}

export interface Props {
  quoteId: string;
  quoteItemId: string;
  value?: DocumentValue | DocumentListValue | null;
  attributeType: QFCTypeExecuted['attributeType'];
  isCollapsed: boolean;
  isDisabled?: boolean;
}

export const FileField = ({ quoteId, quoteItemId, attributeType, value, isCollapsed, isDisabled = false }: Props) => {
  const searchDocumentQueryParams = {
    quoteId,
    pageLimit: 50,
  };
  const { data, isPending: isLoading, isRefetching } = useSearchDocuments(searchDocumentQueryParams);
  const { label, dataType } = attributeType;
  const { createFileAsync } = useCreateFile();
  const { updateFileAsync } = useUpdateFile();
  const { createDocumentAsync } = useCreateDocument();
  const { deleteFileAsync } = useDeleteFile();
  const { deleteDocumentAsync } = useDeleteDocument();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatedDescriptions, setUpdatedDescriptions] = useState<Record<string, string | undefined>>({});
  const { saveFields } = useSaveFields({
    quoteId,
    quoteItemId,
    fields: [
      {
        attributeTypeId: attributeType.id,
        attributeId: attributeType.attribute?.id,
      },
    ],
  });
  const { enqueueSnackbar } = useSnackbar();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const client = useQueryClient();
  const uploadedDocuments = data?.items ?? [];

  let attributeDocuments: DocumentValue['document'][] = [];
  const isDocumentList = dataType === DataType.DocumentList && isDocumentListValue(value);

  if (isDocumentList) {
    attributeDocuments = value?.documents ?? [];
  }

  async function onNewFiles(files: PostArtifact[]) {
    setIsUploading(true);
    const resultingIds = await Promise.all(
      files
        .filter((f) => acceptedFileMimeTypes.includes(f.file.type))
        .map(async (f) => {
          let createdFileId;
          let createdDocumentId;
          try {
            const documentId = cuid();
            const file = await createFileAsync({
              description: f.description,
              awsS3Id: `document/${documentId}`,
              fileName: f.name,
              originalFileName: f.name,
              fileType: f.mimetype,
            });
            if (!file.uploadUrl) {
              console.log('Failed to create valid file, should delete it');
              throw new Error('Invalid file generated');
            }
            createdFileId = file.id;
            await uploadFile(file.uploadUrl, f.file);
            const document = await createDocumentAsync({
              fileId: file.id,
              quoteId,
              documentTypeId: DocumentType.Other.id,
            });
            createdDocumentId = document.id;
            return document.id;
          } catch (e) {
            console.log(e);
            if (createdFileId) {
              deleteFileAsync({ fileId: createdFileId });
            }
            if (createdDocumentId) {
              deleteDocumentAsync({ documentId: createdDocumentId });
            }
            return undefined;
          }
        }),
    );
    client.refetchQueries({ queryKey: documentQueryKey.searchWithParams(searchDocumentQueryParams) });
    return saveFields([
      {
        id: attributeType.attribute?.id,
        attributeTypeId: attributeType.id,
        value: {
          documents: attributeDocuments.concat(
            resultingIds
              .filter((id) => id)
              .map((id) => ({
                documentId: id as string,
              })),
          ),
        },
      },
    ])
      .then(() => {
        enqueueSnackbar('Documents added to Quote', { variant: ToastType.Success });
      })
      .finally(() => {
        setIsUploading(false);
      });
  }
  async function handleDelete({ documentId, documentIndex }: { documentId?: string; documentIndex?: number }) {
    if (isDocumentList) {
      let newValue;
      if (documentId) {
        try {
          await deleteDocumentAsync({ documentId });
          setIsDeleting(true);
        } catch (e) {
          console.warn(
            'Error while attempting to delete quote document, document will still be removed from quoteItemAttribute',
          );
          setIsDeleting(false);
        }

        newValue = {
          documents: attributeDocuments.filter((d) => d.documentId !== documentId) ?? [],
        };
      }
      if (documentIndex !== undefined) {
        newValue = {
          documents: attributeDocuments.filter((_, i) => i !== documentIndex) ?? [],
        };
      }
      if (newValue) {
        saveFields([
          {
            id: attributeType.attribute?.id,
            attributeTypeId: attributeType.id,
            value: newValue,
          },
        ]).then(() => {
          setIsDeleting(false);
          enqueueSnackbar('Document removed from Quote', {
            variant: ToastType.Info,
            autoHideDuration: 5000,
          });
        });
      }
    }
  }

  function updateDescription(fileId?: string, description?: string) {
    if (!fileId) {
      return;
    }
    const newDescription = description?.slice(0, descriptionMaxLength);
    const existingFile = uploadedDocuments.find((ud) => ud.file?.id === fileId);
    if (!existingFile || !existingFile.file) {
      return;
    }
    const isNew = existingFile.file.description !== newDescription;
    setUpdatedDescriptions((state) => ({
      ...state,
      [fileId]: isNew ? newDescription : undefined,
    }));
  }

  function handleBlurDescription(fileId?: string) {
    if (!fileId) {
      return;
    }
    const descriptionToSave = updatedDescriptions[fileId];
    if (descriptionToSave !== undefined) {
      updateFileAsync({ fileId, description: descriptionToSave }).then(() => {
        setUpdatedDescriptions((state) => omit(state, fileId));
        enqueueSnackbar('Document description updated', {
          autoHideDuration: 5000,
          variant: ToastType.Info,
        });
      });
    }
  }
  const placeholderName = isRefetching ? 'Loading...' : 'Missing file';

  if (isLoading) {
    return <Loading />;
  }

  if (isUploading) {
    return <Typography>Uploading files...</Typography>;
  }

  if (isDocumentList) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {showUploadModal ? (
          <AddFilesDialog
            maxFiles={MAX_FILES_AMOUNT}
            onDone={({ uploadedFiles }) => onNewFiles(uploadedFiles)}
            open={showUploadModal}
            handleCloseDialog={() => setShowUploadModal(false)}
          />
        ) : null}
        {attributeDocuments.length === 0 && !isCollapsed ? (
          <Typography variant='bodySmall'>{label ?? 'Upload document(s)'}</Typography>
        ) : null}
        {attributeDocuments.map((document, index) => {
          const uploadedDoc = uploadedDocuments.find((ud) => ud.id === document.documentId);

          return (
            <Fragment key={document.documentId}>
              <FileListItem
                fileName={uploadedDoc?.file?.fileName ? uploadedDoc?.file?.fileName : placeholderName}
                fileType={uploadedDoc?.file?.fileType !== '' ? uploadedDoc?.file?.fileType : 'text/plain'}
                downloadUrl={uploadedDoc?.file?.downloadUrl}
                description={
                  updatedDescriptions[uploadedDoc?.file?.id ?? 'no-key'] ?? uploadedDoc?.file?.description ?? ''
                }
                isCollapsed={isCollapsed}
                onChangeDescription={(value) => updateDescription(uploadedDoc?.file?.id, value)}
                onBlurDescription={() => handleBlurDescription(uploadedDoc?.file?.id)}
                onDelete={() =>
                  handleDelete({
                    documentId: document.documentId,
                    documentIndex: index,
                  })
                }
                isLoading={isDeleting}
              />
              {index !== attributeDocuments.length - 1 ? <Divider /> : null}
            </Fragment>
          );
        })}
        {!isCollapsed ? <UploadFileButton onClick={() => setShowUploadModal(true)} disabled={isDisabled} /> : null}
      </Box>
    );
  }
  return null;
};
